import { request } from "@/api/client";
import type { BankAccount, BankTransaction, BankRule } from "@/types/bank";

export function getBankAccounts(): Promise<BankAccount[]> {
  return request("/bank/accounts");
}

export function createBankAccount(payload: {
  name: string;
  institution?: string;
  account_number_last4?: string;
  currency?: string;
  ledger_account_id: number;
}): Promise<BankAccount> {
  return request("/bank/accounts", { method: "POST", body: payload });
}

export function importBankStatement(accountId: number, file: File): Promise<{ statement_id: string; imported: number; skipped: number }> {
  const body = new FormData();
  body.append("account_id", String(accountId));
  body.append("file", file);
  return request("/bank/import", { method: "POST", body });
}

export function getBankTransactions(params?: {
  status?: string;
  account_id?: number;
  limit?: number;
}): Promise<BankTransaction[]> {
  const query = new URLSearchParams();
  if (params?.status) query.set("status", params.status);
  if (params?.account_id) query.set("account_id", String(params.account_id));
  if (params?.limit) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return request(`/bank/transactions${suffix ? `?${suffix}` : ""}`);
}

export function reconcileBankTransaction(payload: {
  transaction_id: string;
  journal_id: number;
  memo?: string;
  lines: Array<{ account_id: number; debit?: number | string; credit?: number | string; memo?: string }>;
}): Promise<{ transaction_id: string; matched_entry_id: string; match_status: string }> {
  return request("/bank/reconcile", { method: "POST", body: payload });
}

export function matchBankTransaction(transactionId: string, entryId: string): Promise<{ transaction_id: string; matched_entry_id: string; match_status: string }> {
  return request(`/bank/transactions/${encodeURIComponent(transactionId)}/match`, {
    method: "POST",
    body: { entry_id: entryId },
  });
}

export function splitBankTransaction(payload: {
  transaction_id: string;
  journal_id: number;
  splits: Array<{
    amount: number | string;
    memo?: string;
    lines: Array<{ account_id: number; debit?: number | string; credit?: number | string; memo?: string }>;
  }>;
}): Promise<{ transaction_id: string; matched_entry_id: string; match_status: string }> {
  const { transaction_id, ...rest } = payload;
  return request(`/bank/transactions/${encodeURIComponent(transaction_id)}/split`, {
    method: "POST",
    body: { transaction_id, ...rest },
  });
}

export function getBankRules(): Promise<BankRule[]> {
  return request("/bank/rules");
}

export function createBankRule(payload: {
  pattern: string;
  account_id: number;
  memo_template?: string;
  description?: string;
}): Promise<BankRule> {
  return request("/bank/rules", { method: "POST", body: payload });
}

export function createPlaidLinkToken(payload: { channel?: string }): Promise<{ link_token: string; expiration?: string | null }> {
  return request("/bank/plaid/link-token", { method: "POST", body: payload });
}

export function exchangePlaidPublicToken(payload: {
  public_token: string;
  account_id: string;
  account_name?: string | null;
  account_mask?: string | null;
  institution_id?: string | null;
  institution_name?: string | null;
  ledger_account_id: number;
  channel?: string | null;
}): Promise<BankAccount> {
  return request("/bank/plaid/exchange", { method: "POST", body: payload });
}

export function syncPlaidAccount(accountId: number): Promise<{ imported: number; updated: number; removed: number }> {
  return request(`/bank/plaid/accounts/${accountId}/sync`, { method: "POST" });
}
